import { Search, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toggle } from "@/components/ui/toggle";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import {
  filtrosDaBibliotecaAtivos,
  textoDaContagem,
  type FiltrosDaBiblioteca,
} from "../domain/rotulos-da-biblioteca";

type Props = {
  readonly busca: string;
  readonly onBusca: (valor: string) => void;
  readonly filtros: FiltrosDaBiblioteca;
  readonly onFiltros: (proximo: FiltrosDaBiblioteca) => void;
  readonly quantidade: number;
};

export function BarraFiltrosBiblioteca({
  busca,
  onBusca,
  filtros,
  onFiltros,
  quantidade,
}: Props) {
  const ativos = filtrosDaBibliotecaAtivos({ ...filtros, q: busca });

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            value={busca}
            onChange={(evento) => onBusca(evento.target.value)}
            placeholder="Buscar pelo nome"
            aria-label="Buscar na biblioteca"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <ToggleGroup
            type="single"
            value={filtros.tipoArquivo || "todos"}
            onValueChange={(valor) => {
              if (!valor) return;
              onFiltros({
                ...filtros,
                tipoArquivo:
                  valor === "imagem" || valor === "video" ? valor : "",
              });
            }}
            variant="outline"
            size="sm"
            className="justify-start"
            aria-label="Tipo de arquivo"
          >
            <ToggleGroupItem value="todos" className="cursor-pointer px-3">
              Tudo
            </ToggleGroupItem>
            <ToggleGroupItem value="imagem" className="cursor-pointer px-3">
              Imagens
            </ToggleGroupItem>
            <ToggleGroupItem value="video" className="cursor-pointer px-3">
              Vídeos
            </ToggleGroupItem>
          </ToggleGroup>
          <Toggle
            pressed={filtros.soFavoritas}
            onPressedChange={(ligado) =>
              onFiltros({ ...filtros, soFavoritas: ligado })
            }
            variant="outline"
            size="sm"
            className="cursor-pointer px-3"
            aria-label="Só favoritos"
          >
            <Star
              className="size-4"
              aria-hidden
              fill={filtros.soFavoritas ? "currentColor" : "none"}
            />
            Favoritos
          </Toggle>
          <Select
            value={filtros.origem || "todas"}
            onValueChange={(valor) =>
              onFiltros({
                ...filtros,
                origem:
                  valor === "upload" || valor === "publicacao" ? valor : "",
              })
            }
          >
            <SelectTrigger
              className="w-[11.5rem]"
              aria-label="De onde veio o arquivo"
            >
              <SelectValue placeholder="De onde" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">De qualquer lugar</SelectItem>
              <SelectItem value="upload">Enviados por você</SelectItem>
              <SelectItem value="publicacao">Vieram de um post</SelectItem>
            </SelectContent>
          </Select>
          <Select
            value={filtros.ordenar}
            onValueChange={(valor) => {
              if (valor === "recente" || valor === "nome") {
                onFiltros({ ...filtros, ordenar: valor });
              }
            }}
          >
            <SelectTrigger className="w-[10.5rem]" aria-label="Ordenar arquivos">
              <SelectValue placeholder="Ordenar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recente">Mais recentes</SelectItem>
              <SelectItem value="nome">Nome (A a Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {textoDaContagem(quantidade)}
        </p>
        {ativos ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="cursor-pointer"
            onClick={() => {
              onBusca("");
              onFiltros({
                q: "",
                tipoArquivo: "",
                origem: "",
                soFavoritas: false,
                ordenar: "recente",
              });
            }}
          >
            <X className="size-4" aria-hidden />
            Limpar filtros
          </Button>
        ) : null}
      </div>
    </div>
  );
}
